<script setup lang="ts">
import { storeToRefs } from 'pinia';

definePage({ meta: { area: 'marking' } });

const router = useRouter();
const store = useTeacherStore();
const confirm = useConfirmDialog();
const { snackbarSuccess } = useSnackbar();

store.fetchSchool();
store.fetchShareableTeachers();

const { school, shareableTeachers, isLoading } = storeToRefs(store);

if (school.value?.bypass_marking) {
    router.push('/teacher/marking-overview');
}

const selectedIds = ref<number[]>([]);
const saving = ref(false);

watch(
    shareableTeachers,
    (teachers) => {
        selectedIds.value = teachers.filter((t) => t.shared).map((t) => t.id);
    },
    { immediate: true },
);

const originalIds = computed(() => new Set(shareableTeachers.value.filter((t) => t.shared).map((t) => t.id)));

const changes = computed(() => {
    const selected = new Set(selectedIds.value);
    const added = shareableTeachers.value.filter((t) => selected.has(t.id) && !originalIds.value.has(t.id));
    const removed = shareableTeachers.value.filter((t) => !selected.has(t.id) && originalIds.value.has(t.id));
    return { added, removed };
});

const hasChanges = computed(() => changes.value.added.length > 0 || changes.value.removed.length > 0);

function buildConfirmMessage() {
    const lines: string[] = [];
    if (changes.value.added.length > 0) {
        lines.push(`Share marking with: ${changes.value.added.map((t) => t.name).join(', ')}`);
    }
    if (changes.value.removed.length > 0) {
        lines.push(`Stop sharing with: ${changes.value.removed.map((t) => t.name).join(', ')}`);
    }
    return lines.join('\n');
}

async function save() {
    const confirmed = await confirm('Update Sharing', buildConfirmMessage());
    if (!confirmed) return;

    saving.value = true;
    try {
        await store.updateSharedMarking(selectedIds.value);
        snackbarSuccess('Sharing settings updated.');
        router.push('/teacher/marking-overview');
    } finally {
        saving.value = false;
    }
}

function toggleTeacher(teacherId: number) {
    const idx = selectedIds.value.indexOf(teacherId);
    if (idx >= 0) {
        selectedIds.value.splice(idx, 1);
    } else {
        selectedIds.value.push(teacherId);
    }
}
</script>

<template>
    <v-row class="mt-1">
        <v-col cols="12">
            <h1 class="text-brand">Manage Sharing</h1>
            <p class="lead">Select teachers to share your marking with.</p>
        </v-col>

        <v-col cols="12" md="8">
            <v-card>
                <template v-if="isLoading.shareableTeachers || isLoading.sharedMarking">
                    <v-card-text>
                        <v-progress-circular indeterminate color="primary" />
                    </v-card-text>
                </template>
                <template v-else-if="shareableTeachers.length === 0">
                    <v-card-text>
                        <p>There are no other teachers in your school to share marking with.</p>
                    </v-card-text>
                </template>
                <template v-else>
                    <v-list>
                        <v-list-item
                            v-for="teacher in shareableTeachers"
                            :key="teacher.id"
                            @click="toggleTeacher(teacher.id)"
                        >
                            <template #prepend>
                                <v-checkbox-btn
                                    :model-value="selectedIds.includes(teacher.id)"
                                    @update:model-value="toggleTeacher(teacher.id)"
                                    @click.stop
                                />
                            </template>
                            <v-list-item-title>{{ teacher.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </template>

                <v-card-actions>
                    <v-btn
                        variant="flat"
                        color="secondary-container"
                        :disabled="!hasChanges || saving"
                        :loading="saving"
                        @click="save"
                    >
                        Save changes
                    </v-btn>
                    <v-btn variant="text" to="/teacher/marking-overview">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-col>
    </v-row>
</template>
